import axios from "axios";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { apiConfig } from "../../config";
import {
  CreatePaymentMethodDto,
  PaymentMethodResponseDto,
  CaptureSession,
} from "../../types/payment-method";

class PaymentMethodClient {
  private auth0Client: Auth0Client;

  constructor(auth0Client: Auth0Client) {
    this.auth0Client = auth0Client;
  }

  private async getAuthHeaders() {
    const token = await this.auth0Client.getTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async updatePaymentMethod(
    id: string,
    payload: Record<string, unknown>,
  ): Promise<PaymentMethodResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/org/${id}`,
      payload,
      { headers },
    );
    return response.data;
  }

  async getSetupIntent(orgId: string): Promise<CaptureSession> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/setup-intent`,
      { orgId },
      { headers },
    );
    return response.data;
  }

  async getPaymentMethod(orgId: string): Promise<PaymentMethodResponseDto[]> {
    const headers = await this.getAuthHeaders();
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/org/${orgId}`,
      { headers },
    );
    return response.data;
  }

  async deletePaymentMethod(orgId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.delete(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/org/${orgId}`,
      { headers },
    );
    return response.data;
  }

  async deletePaymentMethodById(id: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await axios.delete(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/${id}`,
      { headers },
    );
    return response.data;
  }

  async setDefault(id: string): Promise<PaymentMethodResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.patch(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/${id}/set-default`,
      {},
      { headers },
    );
    return response.data;
  }

  async getDimepayCardStatus(
    token: string,
  ): Promise<{ verified: boolean; status: string; token?: string }> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/payment-method/dimepay/status`,
      { token },
      { headers },
    );
    return response.data;
  }

  async createPaymentMethod(
    payload: CreatePaymentMethodDto,
  ): Promise<PaymentMethodResponseDto> {
    const headers = await this.getAuthHeaders();
    const response = await axios.post(
      `${apiConfig.providerServiceBase}/v2/api/payment-method`,
      payload,
      { headers },
    );
    return response.data;
  }
}

export default PaymentMethodClient;
